import React, { useState, useEffect } from 'react';
import { supabase_insert_vacancy, supabase_get_clients, supabase_get_recruiters } from '../services/supabaseService';
import { VacancyStep, User, UserRole, Client } from '../types';
import {
    Briefcase,
    MapPin,
    Euro,
    GraduationCap,
    Clock,
    Building2,
    Check,
    Loader2,
    ArrowLeft,
    Sparkles,
    HelpCircle,
    Save,
    Trash2,
    Home
} from 'lucide-react';
import AiRichTextEditor from './AiRichTextEditor';
import WerftoolsLogo from './WerftoolsLogo';

interface CreateVacancyProps {
    user: User | null;
    onBack: () => void;
    onSave: (vacancyId: string, isDraft: boolean) => void;
    initialData?: any;
}

// Visualization of the steps, similar to VacancyFlow but simplified for creation view
const PHASES = [
    { name: 'Strategie', steps: ['1. Intake & Analyse', '2. Doelgroep', '3. Propositie'] },
    { name: 'Productie', steps: ['4. Content Creatie', '5. Visuals', '6. Review'] },
    { name: 'Marketing', steps: ['7. Campagne Setup', '8. Distributie', '9. Monitoring'] },
    { name: 'Hiring', steps: ['10. Screening', '11. Interviews', '12. Offer'] }
];

const CreateVacancy: React.FC<CreateVacancyProps> = ({ user, onBack, onSave, initialData }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [clients, setClients] = useState<Client[]>([]);
    const [isLoadingClients, setIsLoadingClients] = useState(false);
    const [recruiters, setRecruiters] = useState<any[]>([]);
    const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false);
    const [clientSettings, setClientSettings] = useState<any>(null);

    useEffect(() => {
        if (user && (user.role === UserRole.ADMIN || user.role === UserRole.RECRUITER || user.role === UserRole.CLIENT)) {
            const fetchClientsAndRecruiters = async () => {
                setIsLoadingClients(true);
                setIsLoadingRecruiters(true);
                try {
                    const { supabase_get_client_settings } = await import('../services/supabaseService');
                    const [clientsData, recruitersData, settingsData] = await Promise.all([
                        supabase_get_clients(),
                        supabase_get_recruiters(user.role === UserRole.CLIENT ? user.clientId : undefined),
                        user.clientId ? supabase_get_client_settings(user.clientId) : Promise.resolve(null)
                    ]);
                    setClients(clientsData || []);
                    setRecruiters(recruitersData || []);
                    if (settingsData) setClientSettings(settingsData);
                } catch (err) {
                    console.error("Failed fetching data", err);
                } finally {
                    setIsLoadingClients(false);
                    setIsLoadingRecruiters(false);
                }
            };
            fetchClientsAndRecruiters();
        }
    }, [user]);

    const [vacancyData, setVacancyData] = useState({
        title: initialData?.title || '',
        client_id: user?.clientId || '',
        opdrachtgever_id: '',
        jobCode: '',
        workplaceType: 'On-site',
        location: '',
        description: initialData?.description || '', // About the role
        requirements: initialData?.requirements || '', // Requirements
        benefits: '', // What we offer
        salary: { min: 0, max: 0, currency: 'EUR', period: 'Yearly' },
        experience: 'Medior',
        education: 'HBO',
        employmentType: 'Fulltime',
        placementTypes: [] as string[],
        assigned_recruiter_id: user?.id || '',
    });

    const [activeSection, setActiveSection] = useState<'details' | 'specs'>('details');

    const handleChange = (field: string, value: any) => {
        setVacancyData(prev => ({ ...prev, [field]: value }));
    };

    const handleSalaryChange = (field: string, value: any) => {
        setVacancyData(prev => ({ ...prev, salary: { ...prev.salary, [field]: value } }));
    };

    const handleSubmit = async (status: string = 'Concept') => {
        if (!vacancyData.title) {
            setError("Functietitel is verplicht.");
            window.scrollTo(0, 0);
            return;
        }

        if (!vacancyData.assigned_recruiter_id) {
            setError("Toegewezen Recruiter is verplicht. Selecteer een recruiter.");
            window.scrollTo(0, 0);
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            const newVacancy = {
                title: vacancyData.title,
                client_id: user?.clientId || '00000000-0000-0000-0000-000000000001', // Fallback
                status: status,
                opdrachtgever_id: vacancyData.opdrachtgever_id || null,
                assigned_recruiter_id: vacancyData.assigned_recruiter_id,
                description: vacancyData.description,
                responsibilities: {},
                data: {
                    clientName: clients.find(c => c.id === vacancyData.opdrachtgever_id)?.name || null,
                    opdrachtgeverLogoUrl: clients.find(c => c.id === vacancyData.opdrachtgever_id)?.logo_url || null,
                    jobCode: vacancyData.jobCode,
                    workplaceType: vacancyData.workplaceType,
                    location: vacancyData.location,
                    salary: vacancyData.salary,
                    experience: vacancyData.experience,
                    education: vacancyData.education,
                    benefits: vacancyData.benefits,
                    category: vacancyData.employmentType, // Map employmentType to category/function loosely for now
                    hoursPerWeek: vacancyData.employmentType,
                    placementTypes: vacancyData.placementTypes,

                    // AI Fields - Empty to trigger generation
                    suggestions: vacancyData.requirements,
                    marketResearch: '',
                    persona: '',
                    finalVacancyContent: ''
                }
            };

            const savedVacancy = await supabase_insert_vacancy(newVacancy);

            if (status === VacancyStep.RESEARCH) {
                // "Save & Continue" -> Go to Flow
                onSave(savedVacancy.id, false);
            } else {
                // "Save Draft" -> Go to List
                onSave(savedVacancy.id, true);
            }

        } catch (e: any) {
            console.error("Error creating vacancy:", e);
            setError(e.message || "Er is een fout opgetreden bij het opslaan.");
            window.scrollTo(0, 0);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                            <Briefcase className="text-[#e51b23] shrink-0" size={24} />
                            Nieuwe Vacature
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => handleSubmit(VacancyStep.INTAKE)}
                            className="px-6 py-2 text-sm font-bold text-white bg-[#e51b23] rounded-lg hover:bg-[#B71C1C] shadow-sm transition-colors flex items-center gap-2"
                            disabled={isSaving}
                        >
                            {isSaving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                            Opslaan & Doorgaan
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">

                {/* LEFT SIDEBAR: Steps Visualization */}
                <div className="lg:col-span-3 space-y-6">
                    <div className="bg-white border border-slate-200 rounded-xl p-4 sticky top-24 shadow-sm">
                        <h3 className="font-bold text-slate-800 mb-4 px-2">Vacancy Flow</h3>
                        <div className="space-y-6">
                            {PHASES.map((phase, pIndex) => (
                                <div key={phase.name} className="relative">
                                    {/* Phase Connector Line */}
                                    {pIndex !== PHASES.length - 1 && (
                                        <div className="absolute left-3 top-8 bottom-[-24px] w-0.5 bg-slate-100" />
                                    )}

                                    <div className="flex items-center gap-3 mb-2">
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border ${pIndex === 0 ? 'bg-red- border-red- text-white' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                                            {pIndex + 1}
                                        </div>
                                        <span className={`text-sm font-bold ${pIndex === 0 ? 'text-red-' : 'text-slate-400'}`}>
                                            {phase.name}
                                        </span>
                                    </div>

                                    <div className="ml-9 space-y-1">
                                        {phase.steps.map((step, sIndex) => (
                                            <div key={step} className={`text-xs py-1 ${pIndex === 0 && sIndex === 0 ? 'text-red- font-medium' : 'text-slate-400'}`}>
                                                {step}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* MAIN CONTENT Area */}
                <div className="lg:col-span-9 space-y-8">

                    {error && (
                        <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-xl text-sm font-bold">
                            {error}
                        </div>
                    )}

                    {/* Section 1: Job Title */}
                    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
                        <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                            <Briefcase className="text-slate-400" size={20} /> Functie & Afdeling
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Functietitel <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={vacancyData.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="bv. Senior Marketing Manager"
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 focus:border-transparent outline-none transition-all font-medium"
                                />
                                <p className="mt-1 text-xs text-slate-500">Kies een duidelijke, gangbare titel voor betere vindbaarheid.</p>
                            </div>

                            {(user?.role === UserRole.ADMIN || user?.role === UserRole.RECRUITER) && (
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Klant <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <select
                                            value={vacancyData.opdrachtgever_id}
                                            onChange={(e) => handleChange('opdrachtgever_id', e.target.value)}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 outline-none transition-all font-medium appearance-none"
                                            disabled={isLoadingClients}
                                        >
                                            <option value="">Selecteer Klant...</option>
                                            {clients.map(client => (
                                                <option key={client.id} value={client.id}>{client.name}</option>
                                            ))}
                                        </select>
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                            <Building2 size={16} />
                                        </div>
                                    </div>
                                    {isLoadingClients && <p className="text-xs text-slate-400 mt-1">Klanten laden...</p>}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Toegewezen Recruiter <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <select
                                        value={vacancyData.assigned_recruiter_id}
                                        onChange={(e) => handleChange('assigned_recruiter_id', e.target.value)}
                                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 outline-none transition-all font-medium appearance-none"
                                        disabled={isLoadingRecruiters}
                                    >
                                        <option value="">Selecteer Recruiter...</option>
                                        {recruiters.map(recruiter => (
                                            <option key={recruiter.id} value={recruiter.id}>{recruiter.name} ({recruiter.email})</option>
                                        ))}
                                    </select>
                                </div>
                                {isLoadingRecruiters && <p className="text-xs text-slate-400 mt-1">Recruiters laden...</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Interne Job Code</label>
                                    <input
                                        type="text"
                                        value={vacancyData.jobCode}
                                        onChange={(e) => handleChange('jobCode', e.target.value)}
                                        placeholder="bv. VAC-2024-001"
                                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 outline-none transition-all font-medium"
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Section: inline save feedback */}
                        <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                            <button
                                onClick={() => handleSubmit(VacancyStep.INTAKE)}
                                disabled={isSaving}
                                className="px-5 py-2 bg-slate-900 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-sm flex items-center gap-2 active:scale-95"
                            >
                                {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                Opslaan
                            </button>
                        </div>
                    </section>

                    {/* Section 2: Location */}
                    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
                        <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                            <MapPin className="text-slate-400" size={20} /> Locatie
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-3">Werkplek Type <span className="text-red-500">*</span></label>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    {['On-site', 'Hybrid', 'Remote'].map((type) => (
                                        <label key={type} className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${vacancyData.workplaceType === type ? 'bg-red- border-red- ring-1 ring-red-0' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'} `}>
                                            <input
                                                type="radio"
                                                name="workplaceType"
                                                value={type}
                                                checked={vacancyData.workplaceType === type}
                                                onChange={(e) => handleChange('workplaceType', e.target.value)}
                                                className="text-red- focus:ring-red-0 h-4 w-4"
                                            />
                                            <span className="font-bold text-slate-700">{type}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Kantoorlocatie / Standplaats</label>
                                <input
                                    type="text"
                                    value={vacancyData.location}
                                    onChange={(e) => handleChange('location', e.target.value)}
                                    placeholder="bv. Amsterdam, Nederland"
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 outline-none transition-all font-medium"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                            <button
                                onClick={() => handleSubmit(VacancyStep.INTAKE)}
                                disabled={isSaving}
                                className="px-5 py-2 bg-slate-900 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-sm flex items-center gap-2 active:scale-95"
                            >
                                {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                Opslaan
                            </button>
                        </div>
                    </section>

                    {/* Section 3: Details */}
                    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
                        <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                            <Sparkles className="text-slate-400" size={20} /> Functieomschrijving
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <AiRichTextEditor
                                    label="Over de rol"
                                    value={vacancyData.description}
                                    onChange={(val) => handleChange('description', val)}
                                    placeholder="Beschrijf de rol, verantwoordelijkheden en uitdagingen..."
                                    minHeight="200px"
                                    defaultAiPrompt={clientSettings?.ai_prompts_settings?.vacatureIntro || ''}
                                />
                            </div>

                            <div>
                                <AiRichTextEditor
                                    label="Vereisten / Requirements"
                                    value={vacancyData.requirements}
                                    onChange={(val) => handleChange('requirements', val)}
                                    placeholder="Wat vragen we? (Opleiding, Ervaring, Skills)"
                                    minHeight="150px"
                                    defaultAiPrompt={clientSettings?.ai_prompts_settings?.vacatureEisen || ''}
                                />
                            </div>

                            <div>
                                <AiRichTextEditor
                                    label="Arbeidsvoorwaarden & Benefits"
                                    value={vacancyData.benefits}
                                    onChange={(val) => handleChange('benefits', val)}
                                    placeholder="Salaris, auto van de zaak, vakantiedagen..."
                                    minHeight="150px"
                                    defaultAiPrompt={clientSettings?.ai_prompts_settings?.vacatureVoordelen || ''}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                            <button
                                onClick={() => handleSubmit(VacancyStep.INTAKE)}
                                disabled={isSaving}
                                className="px-5 py-2 bg-slate-900 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-sm flex items-center gap-2 active:scale-95"
                            >
                                {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                Opslaan
                            </button>
                        </div>
                    </section>

                    {/* Section 4: Employment & Salary */}
                    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
                        <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                            <Euro className="text-slate-400" size={20} /> Contract & Salaris
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Dienstverband</label>
                                <select
                                    value={vacancyData.employmentType}
                                    onChange={(e) => handleChange('employmentType', e.target.value)}
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 outline-none transition-all font-medium"
                                >
                                    <option value="">Selecteer type...</option>
                                    <option value="Fulltime">Fulltime</option>
                                    <option value="Parttime">Parttime</option>
                                    <option value="Freelance">Freelance / ZZP</option>
                                    <option value="Internship">Stage</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Plaatsingsvorm</label>
                                <div className="space-y-2 mt-3">
                                    {['Vaste dienst', 'ZZP', 'IP model'].map((type) => (
                                        <label key={type} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-200 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={vacancyData.placementTypes?.includes(type)}
                                                onChange={(e) => {
                                                    const updated = e.target.checked
                                                        ? [...(vacancyData.placementTypes || []), type]
                                                        : (vacancyData.placementTypes || []).filter(t => t !== type);
                                                    handleChange('placementTypes', updated);
                                                }}
                                                className="w-4 h-4 rounded border-gray-300 text-red-0 focus:ring-red-0"
                                            />
                                            <span className="text-sm font-medium text-slate-700 w-full">{type}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Ervaringsniveau</label>
                                <select
                                    value={vacancyData.experience}
                                    onChange={(e) => handleChange('experience', e.target.value)}
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 outline-none transition-all font-medium"
                                >
                                    <option value="">Selecteer niveau...</option>
                                    <option value="Junior">Starter / Junior</option>
                                    <option value="Medior">Medior</option>
                                    <option value="Senior">Senior</option>
                                    <option value="Expert">Expert / Lead</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Opleidingsniveau</label>
                                <select
                                    value={vacancyData.education}
                                    onChange={(e) => handleChange('education', e.target.value)}
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 outline-none transition-all font-medium"
                                >
                                    <option value="">Selecteer...</option>
                                    <option value="MBO">MBO</option>
                                    <option value="HBO">HBO / Bachelor</option>
                                    <option value="WO">WO / Master</option>
                                    <option value="PhD">PhD</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Salarisindicatie ({vacancyData.salary.currency})</label>
                                <div className="flex gap-2 items-center">
                                    <input
                                        type="number"
                                        placeholder="Min"
                                        value={vacancyData.salary.min}
                                        onChange={(e) => handleSalaryChange('min', e.target.value)}
                                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 outline-none transition-all font-medium"
                                    />
                                    <span className="text-slate-400">-</span>
                                    <input
                                        type="number"
                                        placeholder="Max"
                                        value={vacancyData.salary.max}
                                        onChange={(e) => handleSalaryChange('max', e.target.value)}
                                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-red-0 outline-none transition-all font-medium"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-between items-center mt-6 pt-4 border-t border-slate-100">
                            <p className="text-xs text-slate-400 font-medium">Alle velden worden opgeslagen bij doorgaan.</p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => handleSubmit(VacancyStep.INTAKE)}
                                    disabled={isSaving}
                                    className="px-5 py-2 bg-slate-100 text-slate-700 border border-slate-200 rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all flex items-center gap-2 active:scale-95"
                                >
                                    {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                    Opslaan
                                </button>
                                <button
                                    onClick={() => handleSubmit(VacancyStep.INTAKE)}
                                    disabled={isSaving}
                                    className="px-5 py-2 bg-[#e51b23] text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-[#B71C1C] transition-all shadow-md flex items-center gap-2 active:scale-95"
                                >
                                    {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                    Opslaan & Doorgaan
                                </button>
                            </div>
                        </div>
                    </section>

                </div>
            </main>
        </div>
    );
};

export default CreateVacancy;
